import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import useViewerRole from "@/lib/rights/use-viewer-role";
import { getLineItemRejectedByMerchantQuantity } from "@babackapp/calculations/get-line-item-rejected-by-merchant-quantity";
import { getLineItemValidatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-validated-by-merchant-quantity";
import { getLineItemValidatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-validated-by-logistician-quantity";
import { getLineItemRejectedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-rejected-by-logistician-quantity";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { useCanLogisticianRevalidateAfterMerchant } from "../../hooks/use-can-logistician-revalidate-after-merchant";

type LineItemSectionType =
  | "itemsRejectedByMerchant"
  | "itemsRejectedByLogistician"
  | "itemsValidatedByLogistician"
  | "itemsToShowToHandle"
  | "itemsNotReturned"
  | "itemsValidatedByMerchant"
  | "itemsNotTreatedByLogistician";

/**
 * Split return request items into 3 sections:
 * - itemsRejected: items that have been rejected
 * - itemsToShowToHandle: items that need to be handled
 * - itemsNotToHandle: items that don't need to be handled
 *
 * It updates every time the current return request changes or its cache
 */
export function useRejectLineItemsListSections() {
  const currentReturnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const workflowLogisticianActivated = shop.workflowLogistician.activated;
  const canRevalidateAfterMerchant = useCanLogisticianRevalidateAfterMerchant();

  const getLineItemThatCanBeHandledByTheMerchantQuantity =
    workflowLogisticianActivated
      ? getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician
      : getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician;

  return useMemo(() => {
    return currentReturnRequest.line_items.reduce(
      (acc, lineItem) => {
        const notReturnedQuantity = getLineItemNotReturnedQuantity({
          lineItem,
        });
        if (notReturnedQuantity > 0) {
          acc.itemsNotReturned.push(lineItem);
        }

        // BAB-2943, see the mirror comment in useLineItemsListSections.
        if (canRevalidateAfterMerchant) {
          const notTreatedByLogisticianQuantity =
            getLineItemNotTreatedByLogisticianQuantity({
              lineItem,
              allowRevalidationAfterMerchant: true,
            });
          if (notTreatedByLogisticianQuantity > 0) {
            acc.itemsNotTreatedByLogistician.push(lineItem);
          }
        }

        let managed = 0;

        const validatedByMerchantQuantity =
          getLineItemValidatedByMerchantQuantity({ lineItem });
        if (validatedByMerchantQuantity > 0) {
          managed += validatedByMerchantQuantity;
          acc.itemsValidatedByMerchant.push(lineItem);
          if (managed >= lineItem.quantity) {
            return acc;
          }
        }
        const rejectedByMerchantQuantity =
          getLineItemRejectedByMerchantQuantity({ lineItem });
        if (rejectedByMerchantQuantity > 0) {
          acc.itemsRejectedByMerchant.push(lineItem);
          managed += rejectedByMerchantQuantity;
          if (managed >= lineItem.quantity) {
            return acc;
          }
        }
        if (workflowLogisticianActivated) {
          const validatedByLogisticianQuantity =
            getLineItemValidatedByLogisticianQuantity({ lineItem });
          if (validatedByLogisticianQuantity > 0) {
            managed += validatedByLogisticianQuantity;
            acc.itemsValidatedByLogistician.push(lineItem);
            if (managed >= lineItem.quantity) {
              return acc;
            }
          }
          const rejectedByLogisticianQuantity =
            getLineItemRejectedByLogisticianQuantity({ lineItem });
          if (rejectedByLogisticianQuantity > 0) {
            managed += rejectedByLogisticianQuantity;
            acc.itemsRejectedByLogistician.push(lineItem);
            if (managed >= lineItem.quantity) {
              return acc;
            }
          }
          const notTreatedByLogisticianQuantity =
            getLineItemNotTreatedByLogisticianQuantity({ lineItem });
          if (notTreatedByLogisticianQuantity > 0) {
            managed += notTreatedByLogisticianQuantity;
            // Already pushed above when re-validation is allowed.
            if (!canRevalidateAfterMerchant) {
              acc.itemsNotTreatedByLogistician.push(lineItem);
            }
            if (managed >= lineItem.quantity) {
              return acc;
            }
          }
        }
        const canBeHandledByTheMerchant =
          getLineItemThatCanBeHandledByTheMerchantQuantity({ lineItem });
        if (canBeHandledByTheMerchant > 0) {
          acc.itemsToShowToHandle.push(lineItem);
        }

        return acc;
      },
      {
        itemsRejectedByMerchant: [] as ReturnRequestLineItemDto[],
        itemsRejectedByLogistician: [] as ReturnRequestLineItemDto[],
        itemsValidatedByLogistician: [] as ReturnRequestLineItemDto[],
        itemsToShowToHandle: [] as ReturnRequestLineItemDto[],
        itemsNotReturned: [] as ReturnRequestLineItemDto[],
        itemsNotTreatedByLogistician: [] as ReturnRequestLineItemDto[],
        itemsValidatedByMerchant: [] as ReturnRequestLineItemDto[],
      } as Record<LineItemSectionType, ReturnRequestLineItemDto[]>,
    );
  }, [
    currentReturnRequest.line_items,
    getLineItemThatCanBeHandledByTheMerchantQuantity,
    canRevalidateAfterMerchant,
    workflowLogisticianActivated,
  ]);
}

/**
 * Decides if we should display the validate/reject button on the return request.
 *
 * If an item has to be handled by a merchant/logistician, this button should be displayed.
 */
export function useRejectCurrentReturnRequestHasNothingToHandle() {
  const {
    itemsToShowToHandle,
    itemsNotTreatedByLogistician,
    itemsRejectedByLogistician,
    itemsValidatedByLogistician,
  } = useRejectLineItemsListSections();

  const isLogistician = useViewerRole() === "logistician";
  if (isLogistician) {
    return itemsNotTreatedByLogistician.length === 0;
  }

  const hasItemsToHandle =
    itemsToShowToHandle.length > 0 ||
    itemsNotTreatedByLogistician.length > 0 ||
    itemsRejectedByLogistician.length > 0 ||
    itemsValidatedByLogistician.length > 0;

  return !hasItemsToHandle;
}
